import type { SyncEventPayload, SyncEventType } from "./sync-event";

/**
 * The deterministic "current state" reducer behind the Ano Sync Control Tower.
 *
 * No AI summarization (per the MVP brief): the panel and the GET
 * /api/channels/:id/context endpoint both fold the channel's recent Sync
 * Events through THIS one function so desktop and server never disagree about
 * "what is the state of this work right now".
 */

export type SyncEventRecord = SyncEventPayload & {
  /** The timeline message this event was posted as. */
  messageId: string;
  /** Epoch ms — the message's created_at (authoritative over payload.createdAt). */
  createdAt: number;
};

export type ControlTowerTestStatus = "passed" | "failed" | "mixed" | "unknown";

export type ControlTowerState = {
  /** Most recent event of any type. */
  latest: SyncEventRecord | null;
  /** Most recent completed/failed event. */
  latestCompleted: SyncEventRecord | null;
  /** Most recent in-progress / active event. */
  latestActive: SyncEventRecord | null;
  decisions: string[];
  risks: string[];
  blockers: string[];
  openQuestions: string[];
  nextSteps: string[];
  changedFiles: string[];
  /** Distinct actor names seen across the recent events. */
  agentsAndTools: string[];
  /** Rolled-up status of the most recent event that reported tests. */
  latestTestStatus: ControlTowerTestStatus | null;
};

const ACTIVE_EVENT_TYPES: ReadonlySet<SyncEventType> = new Set<SyncEventType>([
  "work_started",
  "progress",
  "review_requested",
  "blocker",
  "question",
  "tests",
  "decision",
]);

/**
 * Reduce a channel's recent Sync Events into the current Control Tower state.
 * Input may be in any order; we sort newest-first internally.
 *
 * MVP note: risks/blockers accumulate (deduped) across the recent window —
 * a later "completed" event does not auto-clear an earlier risk. That keeps
 * the reducer pure + deterministic; resolving items explicitly is future work.
 */
export function deriveControlTowerState(
  events: SyncEventRecord[],
): ControlTowerState {
  const sorted = [...events].sort((a, b) => b.createdAt - a.createdAt);

  const state: ControlTowerState = {
    latest: sorted[0] ?? null,
    latestCompleted: null,
    latestActive: null,
    decisions: [],
    risks: [],
    blockers: [],
    openQuestions: [],
    nextSteps: [],
    changedFiles: [],
    agentsAndTools: [],
    latestTestStatus: null,
  };

  const decisions = new Set<string>();
  const risks = new Set<string>();
  const blockers = new Set<string>();
  const questions = new Set<string>();
  const nextSteps = new Set<string>();
  const changedFiles = new Set<string>();
  const agents = new Set<string>();

  for (const ev of sorted) {
    if (
      !state.latestCompleted &&
      (ev.eventType === "completed" || ev.eventType === "failed")
    ) {
      state.latestCompleted = ev;
    }
    if (!state.latestActive && ACTIVE_EVENT_TYPES.has(ev.eventType)) {
      state.latestActive = ev;
    }
    if (
      state.latestTestStatus === null &&
      ev.tests !== undefined &&
      ev.tests.length > 0
    ) {
      state.latestTestStatus = summarizeTests(ev.tests);
    }

    ev.decisions?.forEach((d) => decisions.add(d));
    ev.risks?.forEach((r) => risks.add(r));
    ev.blockers?.forEach((b) => blockers.add(b));
    if (ev.eventType === "question") questions.add(ev.title);
    ev.nextSteps?.forEach((n) => nextSteps.add(n));
    ev.changedFiles?.forEach((f) => changedFiles.add(f));
    if (ev.actorName) agents.add(ev.actorName);
  }

  state.decisions = [...decisions];
  state.risks = [...risks];
  state.blockers = [...blockers];
  state.openQuestions = [...questions];
  state.nextSteps = [...nextSteps];
  state.changedFiles = [...changedFiles];
  state.agentsAndTools = [...agents];
  return state;
}

function summarizeTests(
  tests: ReadonlyArray<{ status: string }>,
): ControlTowerTestStatus {
  if (tests.length === 0) return "unknown";
  const hasFail = tests.some((t) => t.status === "failed");
  const hasPass = tests.some((t) => t.status === "passed");
  if (hasFail && hasPass) return "mixed";
  if (hasFail) return "failed";
  if (hasPass) return "passed";
  return "unknown";
}
